import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import { Card, Button, Badge, Select } from '../components/ui';
import { 
  Smartphone, 
  Move, 
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Camera,
  Download,
  Share2,
  Sparkles,
  Image
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ARPreview = () => {
  const { t } = useTranslation();
  const [designs, setDesigns] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [filterStyle, setFilterStyle] = useState('');
  const [loading, setLoading] = useState(true);
  const [arSupported, setArSupported] = useState(false);
  
  // AR Transform state
  const [transform, setTransform] = useState({
    scale: 1,
    rotation: 0,
    x: 0,
    y: 0
  });

  useEffect(() => {
    // Check if WebXR is supported
    if (navigator.xr) {
      navigator.xr.isSessionSupported('immersive-ar').then(supported => {
        setArSupported(supported);
      });
    }
    fetchDesigns();
  }, [filterStyle]);

  const fetchDesigns = async () => {
    try {
      let url = `${API_URL}/api/ar-designs`;
      if (filterStyle) {
        url += `?style=${filterStyle}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setDesigns(data);
      }
    } catch (error) {
      console.error('Failed to fetch designs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScaleUp = () => {
    setTransform(prev => ({ ...prev, scale: Math.min(prev.scale + 0.1, 2) }));
  };

  const handleScaleDown = () => {
    setTransform(prev => ({ ...prev, scale: Math.max(prev.scale - 0.1, 0.3) }));
  };

  const handleRotate = () => {
    setTransform(prev => ({ ...prev, rotation: (prev.rotation + 15) % 360 }));
  };

  const handleReset = () => {
    setTransform({ scale: 1, rotation: 0, x: 0, y: 0 });
  };

  const styles = [
    'Fine Line', 'Geometric', 'Traditional', 'Neo Traditional', 
    'Realistic', 'Watercolor', 'Minimalist', 'Japanese', 'Blackwork'
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Badge className="mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            Beta Özellik
          </Badge>
          <h1 className="font-heading text-3xl mb-2" data-testid="ar-preview-title">
            AR <span className="text-primary">ÖNİZLEME</span>
          </h1>
          <p className="text-muted-foreground">
            Dövme tasarımlarını vücudunuzda görün
          </p>
        </div>

        {/* AR Not Supported Warning */}
        {!arSupported && (
          <Card className="p-4 mb-8 border-yellow-500/30 bg-yellow-500/10">
            <div className="flex items-center gap-3">
              <Smartphone className="w-6 h-6 text-yellow-500" />
              <div>
                <p className="font-medium">Tam AR deneyimi için mobil uygulamamızı kullanın</p>
                <p className="text-sm text-muted-foreground">
                  Web'de 2D önizleme yapabilirsiniz
                </p>
              </div>
            </div>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Preview Area */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <h2 className="font-heading text-xl mb-4">Önizleme Alanı</h2>
              
              {/* Preview Canvas */}
              <div className="relative aspect-video bg-gradient-to-br from-muted to-background-secondary rounded-sm overflow-hidden mb-4">
                {selectedDesign ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <motion.img
                      src={selectedDesign.image_url}
                      alt={selectedDesign.name}
                      style={{
                        transform: `scale(${transform.scale}) rotate(${transform.rotation}deg) translate(${transform.x}px, ${transform.y}px)`,
                      }}
                      drag
                      dragConstraints={{ left: -100, right: 100, top: -100, bottom: 100 }}
                      onDrag={(e, info) => {
                        setTransform(prev => ({
                          ...prev,
                          x: info.offset.x,
                          y: info.offset.y
                        }));
                      }}
                      className="max-h-[80%] cursor-move"
                    />
                    <div className="absolute bottom-4 left-4 right-4 text-center">
                      <p className="text-sm text-muted-foreground">
                        Tasarımı sürükleyerek konumlandırın
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <Image className="w-16 h-16 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Bir tasarım seçin</p>
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-2">
                <Button size="icon" variant="outline" onClick={handleScaleDown}>
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="outline" onClick={handleScaleUp}>
                  <ZoomIn className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="outline" onClick={handleRotate}>
                  <RotateCcw className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="outline" onClick={handleReset}>
                  <Move className="w-4 h-4" />
                </Button>
                <div className="w-px h-8 bg-border mx-2" />
                <Button size="icon" variant="outline">
                  <Camera className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="outline">
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>

              {selectedDesign && (
                <div className="mt-4 p-4 bg-muted rounded-sm">
                  <h3 className="font-medium mb-2">{selectedDesign.name}</h3>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{selectedDesign.style}</Badge>
                    <Badge variant="secondary">{selectedDesign.category}</Badge>
                  </div>
                  {selectedDesign.suggested_placements && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Önerilen yerleşimler: {selectedDesign.suggested_placements.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </Card>
          </div>

          {/* Design Gallery */}
          <div>
            <Card className="p-6">
              <h2 className="font-heading text-xl mb-4">Tasarımlar</h2>
              
              {/* Filter */}
              <Select
                value={filterStyle}
                onChange={(e) => setFilterStyle(e.target.value)}
                className="mb-4"
              >
                <option value="">Tüm Stiller</option>
                {styles.map(style => (
                  <option key={style} value={style}>{style}</option>
                ))}
              </Select>

              {/* Design Grid */}
              <div className="grid grid-cols-2 gap-3 max-h-[500px] overflow-y-auto">
                {designs.map((design) => (
                  <button
                    key={design.design_id}
                    onClick={() => setSelectedDesign(design)}
                    className={`relative aspect-square rounded-sm overflow-hidden border-2 transition-all ${
                      selectedDesign?.design_id === design.design_id
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-transparent hover:border-primary/50'
                    }`}
                  >
                    <img
                      src={design.image_url}
                      alt={design.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-2 left-2 right-2">
                      <p className="text-xs font-medium truncate">{design.name}</p>
                      <p className="text-xs text-muted-foreground">{design.style}</p>
                    </div>
                  </button>
                ))}
              </div>

              {designs.length === 0 && !loading && (
                <p className="text-center text-muted-foreground py-8">
                  Bu filtreye uygun tasarım bulunamadı
                </p>
              )}
            </Card>

            {/* Upload Custom Design */}
            <Card className="p-6 mt-6">
              <h3 className="font-heading text-lg mb-4">Kendi Tasarımınız</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Kendi tasarımınızı yükleyerek AR önizleme yapın
              </p>
              <Button variant="outline" className="w-full">
                <Camera className="w-4 h-4 mr-2" />
                Tasarım Yükle
              </Button>
            </Card>
          </div>
        </div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8"
        >
          <Card className="p-6">
            <h3 className="font-heading text-lg mb-4">Mobil AR Nasıl Çalışır?</h3>
            <div className="grid sm:grid-cols-4 gap-4">
              {[
                { step: '1', title: 'Tasarım Seç', desc: 'Galeriden veya kendi tasarımınızı' },
                { step: '2', title: 'Kamera Aç', desc: 'Dövme yapılacak bölgeyi hedefleyin' },
                { step: '3', title: 'Yerleştir', desc: 'Tasarımı sürükleyerek konumlandırın' },
                { step: '4', title: 'Kaydet', desc: 'Fotoğraf çekin ve paylaşın' },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-primary font-bold">{item.step}</span>
                  </div>
                  <h4 className="font-medium mb-1">{item.title}</h4>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default ARPreview;
